import { useGetMyVetterProfileQuery } from '@codezo/graphql';
import {
	ChevronRight,
	ChevronsUpDown,
	Inbox,
	LogOut,
	Menu,
	Users,
	type LucideIcon,
} from 'lucide-react';
import { useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { CurrencySelector } from '../components/CurrencySelector';
import { NotificationBell } from '../components/NotificationBell';
import { Separator } from '../components/ui/separator';
import { usePageTitle } from '../stores/page-title';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Logo } from '../components/ui/logo';
import { ThemeToggle } from '../components/ui/theme-toggle';
import { useAuth } from '../hooks/useAuth';
import { cn } from '../lib/utils';

/* ── Navigation ── */

type NavEntry = { id: string; label: string; icon: LucideIcon; path: string };

const NAV: NavEntry[] = [
	{ id: 'jobs', label: 'Jobs', icon: Inbox, path: '/jobs' },
	{ id: 'team', label: 'Team', icon: Users, path: '/team' },
];

/* ── Avatar ── */

function Avatar({ name, size = 26 }: { name: string; size?: number }) {
	const initials = name
		.split(' ')
		.map((n) => n[0])
		.slice(0, 2)
		.join('')
		.toUpperCase();
	return (
		<div
			className="flex shrink-0 items-center justify-center rounded-full border border-line-1 bg-surface-fill font-mono font-medium tracking-[0.04em] text-text-2"
			style={{ width: size, height: size, fontSize: size * 0.4 }}
		>
			{initials}
		</div>
	);
}

/* ── Sidebar ── */

function Sidebar({
	activeId,
	onNav,
	supplierName,
	userName,
	email,
	onLogout,
}: {
	activeId: string;
	onNav: (entry: NavEntry) => void;
	supplierName: string;
	userName: string;
	email: string;
	onLogout: () => void;
}) {
	return (
		<aside className="flex h-full flex-col overflow-hidden border-r border-line-1 bg-sidebar">
			<div className="flex items-center gap-2.5 px-4 pt-4 pb-4 text-text-1">
				<Logo />
				<div className="flex min-w-0 flex-col leading-tight">
					<span className="truncate text-base font-medium -tracking-[0.01em]">
						{supplierName}
					</span>
					<span className="text-xs tracking-[0.04em] text-text-3">Supplier portal</span>
				</div>
			</div>

			<nav className="scroll-thin flex-1 overflow-y-auto px-2">
				{NAV.map((entry) => {
					const active = activeId === entry.id;
					const Icon = entry.icon;
					return (
						<button
							key={entry.id}
							onClick={() => onNav(entry)}
							className={cn(
								'relative mb-px flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left font-sans text-sm font-[450] transition-[background,color] duration-[120ms] ease-out',
								active
									? 'bg-nav-active text-text-1'
									: 'text-text-2 hover:bg-nav-hover hover:text-text-1'
							)}
						>
							{active && (
								<span className="absolute top-1/4 bottom-1/4 -left-2 w-0.5 rounded-sm bg-accent" />
							)}
							<Icon
								size={15}
								className={cn('shrink-0', active ? 'text-text-1' : 'text-text-3')}
							/>
							<span className="flex-1">{entry.label}</span>
						</button>
					);
				})}
			</nav>

			<div className="border-t border-line-1 px-2 pt-2.5 pb-3">
				<DropdownMenu>
					<DropdownMenuTrigger className="flex w-full cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-2 text-left transition-colors hover:bg-nav-hover focus-visible:outline-none data-[popup-open]:bg-nav-hover">
						<Avatar name={userName} />
						<div className="min-w-0 flex-1">
							<div className="truncate text-xs font-medium">{userName}</div>
							<div className="truncate text-xs text-text-3">{email}</div>
						</div>
						<ChevronsUpDown size={14} className="shrink-0 text-text-4" />
					</DropdownMenuTrigger>
					<DropdownMenuContent side="top" align="start" className="w-[188px]">
						<DropdownMenuItem variant="destructive" onClick={onLogout}>
							<LogOut size={14} /> Log out
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</div>
		</aside>
	);
}

/* ── Layout ── */

/**
 * The signed-in supplier portal: sidebar and top bar around the portal pages.
 * Link sessions never reach it — see `PortalGate`.
 */
export function PortalLayout() {
	const navigate = useNavigate();
	const location = useLocation();
	const { user, logout } = useAuth();
	const [sidebarOpen, setSidebarOpen] = useState(false);
	const pageTitle = usePageTitle((s) => s.title);

	const { data } = useGetMyVetterProfileQuery({ fetchPolicy: 'no-cache' });
	const profile = data?.getMyVetterProfile;

	const supplierName = profile?.vetterEntity?.name ?? '05 Creation';
	const fullName = profile
		? `${profile.firstName} ${profile.lastName}`.trim()
		: user?.vetterUser
			? `${user.vetterUser.firstName} ${user.vetterUser.lastName}`.trim()
			: '';
	const email = profile?.authenticationUser?.email ?? user?.email ?? '';
	const userName = fullName || email || 'User';

	const active = NAV.find((n) => location.pathname.startsWith(n.path)) ?? NAV[0];

	const handleNav = (entry: NavEntry) => {
		setSidebarOpen(false);
		navigate(entry.path);
	};

	return (
		<div className="relative h-full w-full overflow-hidden bg-canvas font-sans text-text-1 md:grid md:grid-cols-[var(--sidebar-width)_1fr]">
			{sidebarOpen && (
				<div
					className="fixed inset-0 z-40 bg-black/60 md:hidden"
					onClick={() => setSidebarOpen(false)}
				/>
			)}

			{/* Fixed drawer on mobile, static on md+ */}
			<div
				className={cn(
					'fixed inset-y-0 left-0 z-50 w-[var(--sidebar-width)] transition-transform duration-200 ease-out md:static md:translate-x-0',
					sidebarOpen ? 'translate-x-0' : '-translate-x-full'
				)}
			>
				<Sidebar
					activeId={active.id}
					onNav={handleNav}
					supplierName={supplierName}
					userName={userName}
					email={email}
					onLogout={logout}
				/>
			</div>

			<main className="flex h-full flex-col overflow-hidden bg-canvas">
				<div className="flex h-14 shrink-0 items-center gap-4 border-b border-line-1 px-4 md:px-6">
					<button
						onClick={() => setSidebarOpen(true)}
						className="flex size-7 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-surface-hover hover:text-foreground md:hidden"
					>
						<Menu size={18} />
					</button>
					<div className="flex min-w-0 items-center gap-2 text-xs text-text-3">
						<span className="hidden truncate sm:inline">{supplierName}</span>
						<ChevronRight size={12} className="hidden shrink-0 sm:inline" />
						{pageTitle ? (
							<>
								<Link to={active.path} className="shrink-0 hover:text-text-1">
									{active.label}
								</Link>
								<ChevronRight size={12} className="shrink-0" />
								<span className="truncate font-medium text-text-1">
									{pageTitle}
								</span>
							</>
						) : (
							<span className="font-medium text-text-1">{active.label}</span>
						)}
					</div>
					<div className="flex-1" />
					<CurrencySelector />
					<Separator orientation="vertical" className="h-4 self-center bg-line-1" />
					<ThemeToggle />
					<NotificationBell />
				</div>
				<div className="scroll-thin flex-1 overflow-auto">
					<Outlet />
				</div>
			</main>
		</div>
	);
}
